import React, { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { DIALOG_VARIANTS } from "@hyro-components/Dialogs/DialogConsts";
import { Portal, Typography } from "@mui/material";
import Dialog from "@hyro-components/Dialog";
import { SNIPPET_Z_INDEX } from "src/components/widgets/overview/Snippet";
import { FIELD_TYPES } from "@hyro-components/fields/utils";
import Field from "@hyro-components/fields/HyroField";
import { isEmail } from "@utils";
import { SEVERITY_VARIANTS } from "@consts";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@hyro-components/hyro-button/HyroButton.consts";
import CopyToClipboard from "react-copy-to-clipboard";
import CopyLinkIcon from "src/icons/CopyLinkIcon";
import useSnackbar from "@hyro-components/hooks/useSnackbar";
import HyroFeedbackMessage from "@hyro-components/HyroFeedbackMessage";
import { HyroButton } from "@hyro-components";

// eslint-disable-next-line react/display-name
const ShareDialog = forwardRef(({
  open,
  title,
  description,
  copyText,
  submitButtonText,
  submitButtonLoading,
  showCopyButton,
  handleCancel,
  handleSubmit
}, ref) => {
  const [emails, setEmails] = useState([]);
  const [error, setError] = useState(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState(SEVERITY_VARIANTS.SUCCESS);

  useImperativeHandle(ref, () => ({
    notifyUser(message, isError) {
      addSnackbarMessage(message);
      setSnackbarSeverity(isError ? SEVERITY_VARIANTS.ERROR : SEVERITY_VARIANTS.SUCCESS);
    }
  }));

  const [
    isSnackbarOpen,
    snackbarMessage,
    addSnackbarMessage,
    exitSnackbar,
    closeSnackbar
  ] = useSnackbar();

  useEffect(() => {
    if (emails.length === 0) {
      setError(null);
      return;
    }

    const invalidEmails = emails.filter(email => !isEmail(email));

    if (invalidEmails.length > 0) {
      setError(`Invalid email address${invalidEmails.length > 1 ? "es" : ""}: ${invalidEmails.join(", ")}`);
    } else {
      setError(null);
    }
  }, [emails]);

  useEffect(() => {
    if (!open) {
      setError(false);
      setEmails([]);
    }
  }, [open]);

  const handleCopy = (_, result) => {
    if (result) {
      addSnackbarMessage("Share link successfully copied");
      setSnackbarSeverity(SEVERITY_VARIANTS.SUCCESS);
    } else {
      addSnackbarMessage("Action failed. Please contact support.");
      setSnackbarSeverity(SEVERITY_VARIANTS.ERROR);
    }
  };

  const handleSubmitClick = () => {
    const isInvalid = error || emails?.length === 0 || emails.some(email => !isEmail(email));

    if (isInvalid) {
      return;
    }

    handleSubmit(emails);
  };

  return <>
    <Dialog
      showCloseButton
      size="medium"
      title={title}
      variant={DIALOG_VARIANTS.transactional}
      submitButtonLoading={submitButtonLoading}
      handleSubmit={handleSubmitClick}
      handleCancel={handleCancel}
      handleClose={handleCancel}
      open={open}
      disableSave={!!error}
      submitButtonText={submitButtonText}
      testId="share-dialog"
      additionalButton={
        showCopyButton && copyText ? <CopyToClipboard text={copyText} onCopy={handleCopy}>
          <HyroButton sx={{ marginRight: "auto" }} color={BUTTON_COLORS.PRIMARY} variant={BUTTON_VARIANTS.TEXT} data-testid="share-dialog-copy-link" startIcon={<CopyLinkIcon />}>Copy link</HyroButton>
        </CopyToClipboard> : null
      }
    >
      <Typography variant="body1" sx={{ mb: "18px" }}>{description}</Typography>
      <Field sx={{ zIndex: SNIPPET_Z_INDEX + 1 }}
        showAllChips
        label="Recipient's email address"
        fieldType={FIELD_TYPES.LIST}
        value={emails}
        error={error}
        showTagsInPopper={false}
        testId="share-dialog-email-field"
        helperText="To add multiple emails, type an email and hit Enter."
        onChange={setEmails} />
    </Dialog>

    <Portal>
      <HyroFeedbackMessage
        sx={{ zIndex: SNIPPET_Z_INDEX + 1 }}
        message={snackbarMessage}
        severity={snackbarSeverity}
        showFeedback={isSnackbarOpen}
        onExited={exitSnackbar}
        closeFeedback={closeSnackbar}
      />
    </Portal>
  </>;
});

export default ShareDialog;
